import { PhoneNumberFormat, PhoneNumberType, PhoneNumberUtil } from 'google-libphonenumber';
import { CountryCode, getCountryForTimezone } from 'countries-and-timezones';
import { RuleObject } from 'antd/lib/form';
import { TFunction } from 'i18next';

export const DEFAULT_ERROR_MESSAGE = 'visitFields.phone.phoneNumberValidation.defaultMessage';
export const TOO_SHORT_ERROR_MESSAGE = 'visitFields.phone.phoneNumberValidation.phoneNumberTooShort';
export const TOO_LONG_ERROR_MESSAGE = 'visitFields.phone.phoneNumberValidation.phoneNumberTooLong';
export const INVALID_COUNTRY_ERROR_MESSAGE = 'visitFields.phone.phoneNumberValidation.invalidCountry';
export const LANDLINE_ERROR_MESSAGE = 'visitFields.phone.phoneNumberValidation.landlineWarning';

export class LandlinePhoneNumberException extends Error {}

export class InvalidPhoneNumberException extends Error {
  constructor(errorMessage: PhoneNumberUtil.ValidationResult) {
    super(errorMessage.toString());
  }
}

const replaceErrorMessage = (t: TFunction, validationResult: PhoneNumberUtil.ValidationResult): string => {
  switch (validationResult) {
    case PhoneNumberUtil.ValidationResult.TOO_SHORT:
      return t(TOO_SHORT_ERROR_MESSAGE);
    case PhoneNumberUtil.ValidationResult.TOO_LONG:
      return t(TOO_LONG_ERROR_MESSAGE);
    case PhoneNumberUtil.ValidationResult.INVALID_COUNTRY_CODE:
      return t(INVALID_COUNTRY_ERROR_MESSAGE);
    default:
      return t(DEFAULT_ERROR_MESSAGE);
  }
};

const phoneUtil = PhoneNumberUtil.getInstance();

// In order for the parser to handle the UI provided country, we need to prepend '+' if the '+' isn't already present
export const normalizePhoneNumber = (phoneNumber: string) => phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;

const parsePhoneNumber = (phoneNumber: string) => {
  const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
  return phoneUtil.parseAndKeepRawInput(normalizedPhoneNumber.trim());
};

// countryCode may be based on IP, or from the phone number UI in the country picker,
// if this is not available, we will attempt to use the browser/os timezone to extrapolate country
const getCountry = (countryCode?: string) => (countryCode
  ? countryCode.toUpperCase()
  : getCountryForTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)?.id) as CountryCode;

const parsePhoneNumberWithCountryCode = (phoneNumber: string, countryCode?: string) => {
  const countryCodeToUse = getCountry(countryCode);

  if (countryCodeToUse) {
    return phoneUtil.parseAndKeepRawInput(phoneNumber, countryCodeToUse);
  }

  // if we don't have a countryCode but try to pass it undefined to parse it will always fail,
  // this is the fallback when IP, timezone, or UI selection are somehow unavailable
  // We prepend the + as it otherwise will not interpret the beginning as country
  return parsePhoneNumber(phoneNumber);
};

export const isLandline = (phoneNumber: string): boolean => {
  try {
    const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
    const rawNumber = phoneUtil.parseAndKeepRawInput(normalizedPhoneNumber.trim());
    return phoneUtil.getNumberType(rawNumber) === PhoneNumberType.FIXED_LINE;
  } catch {
    return false;
  }
};

// This method is used by our PhoneInput component, and already inflates the number with a country code
export const validatePhone = (t: TFunction) => (_: RuleObject, phoneNumber: string) => {
  try {
    if (phoneNumber) {
      const rawNumber = parsePhoneNumber(phoneNumber);

      // isPossibleNumberWithReason gives us IS_POSSIBLE_LOCAL_ONLY (7-digit legacy numbers)
      // which isPossibleNumber() incorrectly treats as valid. We reject anything that isn't
      // a fully-internationally-dialable IS_POSSIBLE number, since local-only numbers can't
      // receive SMS messages.
      const validationResult = phoneUtil.isPossibleNumberWithReason(rawNumber);
      if (validationResult !== PhoneNumberUtil.ValidationResult.IS_POSSIBLE) {
        throw new InvalidPhoneNumberException(validationResult);
      }
    }
  } catch (e) {  
    return Promise.reject(replaceErrorMessage(t, parseInt(e.message)));
  }

  return Promise.resolve();
};

export const validateLandline = (t: TFunction) => (_: RuleObject, phoneNumber: string) => {
  try {
    if (phoneNumber && isLandline(phoneNumber)) {
      throw new LandlinePhoneNumberException();
    }
  } catch (e) {
    if (e instanceof LandlinePhoneNumberException) {
      return Promise.reject(t(LANDLINE_ERROR_MESSAGE));
    }
  }
  return Promise.resolve();
};

export function validatePhoneWithCountryCode(phoneNumber: string, countryCode?: string | undefined) {
  try {
    const parsedNumber = parsePhoneNumberWithCountryCode(phoneNumber, countryCode);
    return phoneUtil.isPossibleNumberWithReason(parsedNumber) === PhoneNumberUtil.ValidationResult.IS_POSSIBLE;
  } catch (e) {
    return false;
  }
}

export function formatPhoneWithCountryCode(phoneNumber: string, countryCode?: string) {
  try {
    const parsedNumber = parsePhoneNumberWithCountryCode(phoneNumber, countryCode);
    return phoneUtil.format(parsedNumber, PhoneNumberFormat.E164);
  } catch (e) {
    return phoneNumber;
  }
}

export function formatPhoneToInternational(phoneNumber: string) {
  try {
    const rawNumber = phoneUtil.parseAndKeepRawInput(phoneNumber);
    return phoneUtil.format(rawNumber, PhoneNumberFormat.INTERNATIONAL);
  } catch {
    // if parse fails, return un-formatted number
    return phoneNumber;
  }
}
